import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useClientOptions } from './Invoices';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDate, formatMoney, formatNumber, isoDate } from '../lib/format';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  ProgressBar,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
} from '../components/ui';

/** Sentinel's categorical series — the `chart` scale from the design tokens. */
const SERIES = ['#C7102E', '#22D3EE', '#F9C751', '#7C5CFF', '#17B980', '#FF7A45', '#76777A', '#EE5468'];

/**
 * The finance reports. Operations and workforce reports stayed in the admin;
 * these four came here with the money they describe.
 */
export function ReportsPage() {
  const { t } = useI18n();
  const [tab, setTab] = useState('finance');
  const firstOfMonth = `${new Date().toISOString().slice(0, 7)}-01`;
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(isoDate());

  return (
    <>
      <PageHeader
        title={t('reports.title')}
        action={
          <div className="flex items-center gap-2">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="!w-auto" />
            <span className="text-muted">—</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="!w-auto" />
          </div>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'finance', label: t('reports.finance') },
          // CR-001 §38 — the client-centric finance reports.
          { id: 'revenue', label: t('reports.revenue') },
          { id: 'receivables', label: t('reports.receivables') },
          { id: 'statement', label: t('reports.clientStatement') },
        ]}
      />

      <div className="mt-5">
        {tab === 'finance' && <FinanceReport from={from} to={to} />}
        {tab === 'revenue' && <RevenueReport from={from} to={to} />}
        {tab === 'receivables' && <ReceivablesReport />}
        {tab === 'statement' && <ClientStatementReport from={from} to={to} />}
      </div>
    </>
  );
}

function FinanceReport({ from, to }: { from: string; to: string }) {
  const { t, locale, localized } = useI18n();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports', 'finance', from, to],
    queryFn: () => api.get<any>('/reports/finance', { from, to }),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title="Expenses by category" />
          <ChartPie
            rows={data.expensesByCategory.map((r: any) => ({ label: localized(r.category), value: r.total }))}
            money
          />
        </Card>

        <Card padded={false}>
          <CardHeader
            title="Receivables & payables"
            subtitle={t('reports.inBaseCurrency', { currency: data.invoices.currency })}
            className="p-5 pb-0"
          />
          <Table>
            <thead>
              <tr>
                <Th>{t('common.status')}</Th>
                <Th align="end">{t('common.total')}</Th>
                <Th align="end">{t('invoices.remaining')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.rows.map((row: any, index: number) => (
                <Tr key={index}>
                  <Td>
                    <span className="text-muted">{enumLabel(row.status, locale)}</span>
                    <span className="ms-2 font-mono text-mono-sm text-[var(--text-tertiary)]">
                      {row.currencies.join(' ')}
                    </span>
                  </Td>
                  <Td align="end" className="tabular">
                    {formatMoney(row.total, data.invoices.currency, locale)}
                  </Td>
                  <Td align="end" className="tabular text-warning-fg dark:text-warning-dark">
                    {formatMoney(row.outstanding, data.invoices.currency, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </div>

      <Card padded={false}>
        <CardHeader title="Payroll summary" className="p-5 pb-0" />
        {data.payroll.length ? (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.month')}</Th>
                <Th>{t('common.status')}</Th>
                <Th align="end">{t('payroll.employees')}</Th>
                <Th align="end">{t('payroll.gross')}</Th>
                <Th align="end">{t('payroll.slaPenalties')}</Th>
                <Th align="end">{t('payroll.net')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.payroll.map((p: any) => (
                <Tr key={p.id}>
                  <Td className="tabular">{p.year}-{String(p.month).padStart(2, '0')}</Td>
                  <Td><Badge tone="neutral">{enumLabel(p.status, locale)}</Badge></Td>
                  <Td align="end" className="tabular">{p.employees}</Td>
                  <Td align="end" className="tabular">{formatMoney(p.gross, 'EGP', locale)}</Td>
                  <Td align="end" className="tabular text-error-fg dark:text-error-dark">{formatMoney(p.penalties, 'EGP', locale)}</Td>
                  <Td align="end" className="tabular font-semibold">{formatMoney(p.net, 'EGP', locale)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState />
        )}
      </Card>

      <Card>
        <CardHeader title="Budget vs actual" />
        {data.budgets.length ? (
          <ul className="space-y-4">
            {data.budgets.map((b: any) => (
              <li key={b.budgetId}>
                <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-body-sm text-[var(--text-primary)]">{b.name}</span>
                  <span className="text-body-sm tabular text-muted">
                    {formatMoney(b.actualSpent, b.currency, locale)} / {formatMoney(b.amount, b.currency, locale)}
                  </span>
                </div>
                <ProgressBar
                  percent={b.percentUsed}
                  tone={b.limitExceeded ? 'error' : b.thresholdExceeded ? 'warning' : 'success'}
                />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState />
        )}
      </Card>
    </div>
  );
}

function ChartPie({ rows, money = false }: { rows: Array<{ label: string; value: number }>; money?: boolean }) {
  const { t, locale } = useI18n();
  if (!rows.length) return <EmptyState title={t('common.noResults')} hint="" />;

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={rows} dataKey="value" nameKey="label" innerRadius={50} outerRadius={85} paddingAngle={2}>
            {rows.map((_, index) => (
              <Cell key={index} fill={SERIES[index % SERIES.length]} stroke="var(--surface-card)" strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: any) => (money ? formatMoney(value, 'EGP', locale) : formatNumber(value, locale))}
            contentStyle={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--line)',
              borderRadius: 10,
              color: 'var(--text-primary)',
              fontSize: 13,
            }}
          />
          <Legend
            verticalAlign="bottom"
            iconType="circle"
            wrapperStyle={{ fontSize: 12, color: 'var(--text-secondary)' }}
            // Recharts paints each label in its series' colour, which a dark card or a
            // pale series cannot carry: the dot keeps the colour, the words stay readable.
            formatter={(value: any) => <span className="text-[var(--text-secondary)]">{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* CR-001 §38 — the client-centric finance reports                            */
/* -------------------------------------------------------------------------- */

/**
 * Revenue means money actually collected, converted into the base currency on
 * the day it arrived (§30). That is the only figure that can be added up across
 * clients who pay in different currencies, so every chart below is in it.
 */
function RevenueReport({ from, to }: { from: string; to: string }) {
  const { t, locale } = useI18n();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports-revenue', from, to],
    queryFn: () => api.get<any>('/reports/revenue', { from, to }),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  // Every `local` figure below is in the company base currency (§6), which the
  // server names — it is a setting, not something to infer from a row.
  const base: string = data.baseCurrency;
  const total = data.byClient.reduce((sum: number, r: any) => sum + r.local, 0);

  return (
    <div className="grid gap-5">
      <div className="grid gap-4 sm:grid-cols-3">
        <Metric label={t('reports.collected')} value={formatMoney(total, base, locale)} tone="success" />
        <Metric label={t('reports.byClient')} value={data.byClient.length} />
        <Metric label={t('reports.byCountry')} value={data.byCountry.length} />
      </div>

      {/* §37 — invoiced, collected and outstanding in the base currency, with
          the currencies they were raised in named beside them. */}
      <Card padded={false}>
        <CardHeader
          title={t('reports.byCurrency')}
          subtitle={t('reports.inBaseCurrency', { currency: base })}
          className="p-5 pb-0"
        />
        {!data.invoices ? (
          <EmptyState />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('reports.billedIn')}</Th>
                <Th align="end">{t('nav.invoices')}</Th>
                <Th align="end">{t('reports.invoiced')}</Th>
                <Th align="end">{t('reports.collected')}</Th>
                <Th align="end">{t('reports.outstanding')}</Th>
              </tr>
            </thead>
            <tbody>
              <Tr>
                <Td>
                  <span className="font-mono text-mono-sm text-muted">
                    {data.billedInCurrencies.join('  ')}
                  </span>
                </Td>
                <Td align="end" className="tabular text-muted">{data.invoices}</Td>
                <Td align="end" className="tabular">{formatMoney(data.invoiced, base, locale)}</Td>
                <Td align="end" className="tabular text-success-fg dark:text-success">
                  {formatMoney(data.collected, base, locale)}
                </Td>
                <Td align="end" className={data.outstanding > 0 ? 'tabular text-warning-fg dark:text-warning-dark' : 'tabular'}>
                  {formatMoney(data.outstanding, base, locale)}
                </Td>
              </Tr>
            </tbody>
          </Table>
        )}
        {!!data.unconvertedCurrencies?.length && (
          <p className="p-5 pt-3 text-caption text-warning-fg dark:text-warning-dark">
            {t('reports.noRateWarning', { currencies: data.unconvertedCurrencies.join(', ') })}
          </p>
        )}
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('reports.byClient')} />
          {!data.byClient.length ? (
            <EmptyState />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, data.byClient.length * 34)}>
              <BarChart data={data.byClient} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                <XAxis type="number" stroke="var(--text-tertiary)" fontSize={12} />
                <YAxis type="category" dataKey="name" width={140} stroke="var(--text-tertiary)" fontSize={12} />
                <Tooltip formatter={(v: any) => formatMoney(Number(v), base, locale)} />
                <Bar dataKey="local" fill={SERIES[0]} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <CardHeader title={t('reports.byCountry')} />
          {!data.byCountry.length ? (
            <EmptyState />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('clients.country')}</Th>
                  <Th align="end">{t('clients.title')}</Th>
                  <Th align="end">{t('reports.collected')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.byCountry.map((row: any) => (
                  <Tr key={row.code}>
                    <Td>{locale === 'ar' && row.nameAr ? row.nameAr : row.name}</Td>
                    <Td align="end" className="tabular text-muted">{row.clients}</Td>
                    <Td align="end" className="tabular">{formatMoney(row.local, base, locale)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>

      <Card padded={false}>
        <CardHeader title={t('reports.byProject')} className="p-5 pb-0" />
        {!data.byProject.length ? (
          <EmptyState />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.code')}</Th>
                <Th>{t('common.name')}</Th>
                <Th>{t('invoices.client')}</Th>
                <Th align="end">{t('reports.collected')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.byProject.map((row: any) => (
                <Tr key={row.id}>
                  <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{row.code}</span></Td>
                  <Td>{row.name}</Td>
                  <Td className="text-muted">{row.client ?? '—'}</Td>
                  <Td align="end" className="tabular">{formatMoney(row.local, base, locale)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader title={t('reports.byPeriod')} />
        {!data.byPeriod.length ? (
          <EmptyState />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.byPeriod}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis
                dataKey="month"
                stroke="var(--text-tertiary)"
                fontSize={12}
                tickFormatter={(v: string) => String(v).slice(0, 7)}
              />
              <YAxis stroke="var(--text-tertiary)" fontSize={12} />
              <Tooltip formatter={(v: any) => formatMoney(Number(v), base, locale)} />
              <Bar dataKey="local" fill={SERIES[1]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}

/** §38 — outstanding invoices and the aging buckets. Unbounded by date: what is owed is owed. */
function ReceivablesReport() {
  const { t, locale } = useI18n();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports-receivables'],
    queryFn: () => api.get<any>('/reports/receivables'),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const bucketLabels: Record<string, string> = {
    CURRENT: t('reports.agingCurrent'),
    D1_30: t('reports.aging1_30'),
    D31_60: t('reports.aging31_60'),
    D61_90: t('reports.aging61_90'),
    D90_PLUS: t('reports.aging90'),
  };

  return (
    <div className="grid gap-5">
      <Card padded={false}>
        <CardHeader
          title={t('reports.aging')}
          subtitle={t('reports.inBaseCurrency', { currency: data.aging.currency })}
          className="p-5 pb-0"
        />
        {!data.aging.byClient.length ? (
          <EmptyState />
        ) : (
          <Table>
            <thead>
              <tr>
                {data.aging.buckets.map((b: string) => (
                  <Th key={b} align="end">{bucketLabels[b]}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Tr>
                {data.aging.buckets.map((b: string) => (
                  <Td
                    key={b}
                    align="end"
                    className={b === 'D90_PLUS' && data.aging.totals[b] > 0 ? 'tabular text-error-fg dark:text-error-dark' : 'tabular'}
                  >
                    {formatMoney(data.aging.totals[b], data.aging.currency, locale)}
                  </Td>
                ))}
              </Tr>
            </tbody>
          </Table>
        )}
        {!!data.aging.unconvertedCurrencies?.length && (
          <p className="p-5 pt-3 text-caption text-warning-fg dark:text-warning-dark">
            {t('reports.noRateWarning', { currencies: data.aging.unconvertedCurrencies.join(', ') })}
          </p>
        )}
      </Card>

      <Card padded={false}>
        <CardHeader title={`${t('reports.aging')} — ${t('reports.byClient')}`} className="p-5 pb-0" />
        {!data.aging.byClient.length ? (
          <EmptyState />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('invoices.client')}</Th>
                <Th>{t('reports.billedIn')}</Th>
                {data.aging.buckets.map((b: string) => (
                  <Th key={b} align="end">{bucketLabels[b]}</Th>
                ))}
                <Th align="end">{t('common.total')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.aging.byClient.map((row: any) => (
                <Tr key={row.client.id}>
                  <Td>
                    <Link to={`/clients/${row.client.id}`} className="hover:underline">
                      {locale === 'ar' && row.client.companyNameAr ? row.client.companyNameAr : row.client.companyName}
                    </Link>
                  </Td>
                  <Td>
                    <span className="font-mono text-mono-sm text-muted">{row.currencies.join(' ')}</span>
                  </Td>
                  {data.aging.buckets.map((b: string) => (
                    <Td key={b} align="end" className="tabular text-muted">
                      {row[b] > 0 ? formatMoney(row[b], data.aging.currency, locale) : '—'}
                    </Td>
                  ))}
                  <Td align="end" className="tabular">
                    <strong>{formatMoney(row.total, data.aging.currency, locale)}</strong>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card padded={false}>
        <CardHeader
          title={t('reports.outstandingInvoices')}
          subtitle={`${t('reports.inBaseCurrency', { currency: data.outstanding.currency })} · ${formatMoney(
            data.outstanding.totalRemainingLocal,
            data.outstanding.currency,
            locale,
          )}`}
          className="p-5 pb-0"
        />
        {!data.outstanding.rows.length ? (
          <EmptyState />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('tickets.number')}</Th>
                <Th>{t('invoices.client')}</Th>
                <Th>{t('invoices.dueDate')}</Th>
                <Th align="end">{t('reports.daysOverdue')}</Th>
                <Th align="end">{t('reports.sourceAmount')}</Th>
                <Th align="end">{t('invoices.remaining')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.outstanding.rows.map((inv: any) => (
                <Tr key={inv.id}>
                  <Td>
                    <Link to={`/invoices/${inv.id}`} className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">
                      {inv.number}
                    </Link>
                  </Td>
                  <Td>
                    {locale === 'ar' && inv.client.companyNameAr ? inv.client.companyNameAr : inv.client.companyName}
                  </Td>
                  <Td className="tabular text-muted">{formatDate(inv.dueDate, locale)}</Td>
                  <Td align="end" className={inv.daysOverdue > 0 ? 'tabular text-error-fg dark:text-error-dark' : 'tabular text-muted'}>
                    {inv.daysOverdue > 0 ? inv.daysOverdue : '—'}
                  </Td>
                  <Td align="end" className="tabular text-muted">
                    {formatMoney(inv.remainingAmount, inv.currency, locale)}
                  </Td>
                  <Td align="end" className="tabular">
                    {inv.remainingLocalAmount === null
                      ? '—'
                      : formatMoney(inv.remainingLocalAmount, inv.localCurrency, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/** §38 "Client Statement" — one client's invoices and payments as a running ledger. */
function ClientStatementReport({ from, to }: { from: string; to: string }) {
  const { t, locale } = useI18n();
  const [clientId, setClientId] = useState('');
  const clients = useClientOptions();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports-statement', clientId, from, to],
    queryFn: () => api.get<any>(`/reports/client-statement/${clientId}`, { from, to }),
    enabled: !!clientId,
  });

  return (
    <div className="grid gap-5">
      <Card>
        <Field label={t('invoices.client')} required>
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">—</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
              </option>
            ))}
          </Select>
        </Field>
      </Card>

      {!clientId ? null : isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <>
          {data.localCurrency && (
            <Metric
              label={t('clients.collectedLocal')}
              value={formatMoney(data.collectedLocalAmount, data.localCurrency, locale)}
              tone="success"
            />
          )}
          {!data.entries.length ? (
            <Card><EmptyState /></Card>
          ) : (
            <Card padded={false}>
              <CardHeader
                title={t('clients.statement')}
                subtitle={t('reports.inBaseCurrency', { currency: data.currency })}
                className="p-5 pb-0"
                action={
                  <span className="text-body-sm tabular text-muted">
                    {t('reports.balance')}: {formatMoney(data.closingBalance, data.currency, locale)}
                  </span>
                }
              />
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.date')}</Th>
                    <Th>{t('common.type')}</Th>
                    <Th>{t('payments.reference')}</Th>
                    <Th align="end">{t('reports.sourceAmount')}</Th>
                    <Th align="end">{t('reports.rate')}</Th>
                    <Th align="end">{t('reports.debit')}</Th>
                    <Th align="end">{t('reports.credit')}</Th>
                    <Th align="end">{t('reports.balance')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((entry: any, index: number) => (
                    <Tr key={`${entry.reference}-${index}`}>
                      <Td className="tabular text-muted">{formatDate(entry.date, locale)}</Td>
                      <Td>
                        <Badge tone={entry.kind === 'INVOICE' ? 'neutral' : 'success'}>
                          {entry.kind === 'INVOICE' ? t('nav.invoices') : t('nav.payments')}
                        </Badge>
                      </Td>
                      <Td>
                        <span className="font-mono text-mono-sm">{entry.reference}</span>
                        {entry.description && (
                          <span className="ms-2 text-caption text-muted">{entry.description}</span>
                        )}
                      </Td>
                      {/* What the row was actually raised in — the columns to the
                          right are the same money in the base currency. */}
                      <Td align="end" className="tabular text-muted">
                        {formatMoney(entry.sourceAmount, entry.sourceCurrency, locale)}
                      </Td>
                      <Td align="end" className="tabular text-muted">
                        {entry.sourceCurrency === data.currency ? '—' : (entry.exchangeRate ?? '—')}
                      </Td>
                      <Td align="end" className="tabular">
                        {entry.debit ? formatMoney(entry.debit, data.currency, locale) : '—'}
                      </Td>
                      <Td align="end" className="tabular text-success-fg dark:text-success">
                        {entry.credit ? formatMoney(entry.credit, data.currency, locale) : '—'}
                      </Td>
                      <Td align="end" className="tabular">
                        <strong>{formatMoney(entry.balance, data.currency, locale)}</strong>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
              {!!data.unconvertedCurrencies?.length && (
                <p className="p-5 pt-3 text-caption text-warning-fg dark:text-warning-dark">
                  {t('reports.noRateWarning', { currencies: data.unconvertedCurrencies.join(', ') })}
                </p>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
